/**
 * S'asseoir sur une chaise ou un fauteuil (partagé par le jeu et le serveur
 * temps réel, sous-chemin `@campus/core/seats`).
 *
 * Un siège est un meuble « assise » vu d'une direction : `…face`, `…dos`,
 * `…droite`, `…gauche` (chaises et fauteuils du monde du campus `ch_mb-*`,
 * fauteuils de bureau et gamer dessinés pour Placio `pl_fauteuil_*`). Les vues
 * de dos reconstruites des canapés LimeZu (`lz_…_dos`) n'en sont pas. L'avatar
 * s'y assoit tourné comme le siège (de face = vers le bas). État gardé par la
 * ZoneRoom seulement (`PlayerState.seat`) : un siège ne porte qu'un avatar,
 * rien en base.
 */

export type SeatDirection = 'up' | 'down' | 'left' | 'right';

/** Distance max (px) entre l'avatar et le siège pour s'y asseoir (le client propose à 48 px). */
export const SEAT_REACH = 56;
/** Relèvement max (px) de l'avatar assis accepté par le serveur. */
export const SEAT_LIFT_MAX = 24;

const VIEWS: Record<string, SeatDirection> = { face: 'down', dos: 'up', droite: 'right', gauche: 'left' };
const VIEW_SUFFIX = /(?:^|[-_])(face|dos|droite|gauche)$/;

/** Direction d'un siège, null pour tout autre meuble (catégorie autre que « assise », vue inconnue, LimeZu). */
export function seatDirection(furnitureId: string, category: string | undefined): SeatDirection | null {
  if (category !== 'seating' || furnitureId.startsWith('lz_')) return null;
  const m = VIEW_SUFFIX.exec(furnitureId);
  return m ? VIEWS[m[1]!] ?? null : null;
}

/**
 * Où s'assoit l'avatar sur chaque siège (réglé le 2026-10-21 en posant la pose
 * assise de Charles sur chaque image) : point des hanches dans l'image du
 * siège, sur le dessus du coussin, contre le dossier pour un siège de profil.
 * Dans la pose (case de 48 px), les hanches sont 12 px au-dessus du bas de la
 * case, et 4 px derrière le milieu de profil ; le jeu pose le bas de la case
 * 3 px sous l'ancre (`PC_FEET`), d'où des hanches 9 px au-dessus de l'ancre.
 * Un canapé a une place par coussin.
 */
const HIPS: Record<string, Array<[number, number]>> = {
  'ch_cc-chaise-face': [[16, 17]], 'ch_cc-chaise-dos': [[16, 12]],
  'ch_cc-chaise-droite': [[13, 18]], 'ch_cc-chaise-gauche': [[18, 18]],
  'ch_cc-fauteuil-club-face': [[32, 38]], 'ch_cc-fauteuil-club-droite': [[26, 35]], 'ch_cc-fauteuil-club-gauche': [[37, 35]],
  'ch_cc-fauteuil-accueil-face': [[16, 46]], 'ch_cc-pouf-face': [[16, 12]],
  'ch_cc-canape-face': [[25, 34], [48, 34], [71, 34]], 'ch_cc-canape-deux-droite': [[26, 34], [26, 64]],
  // Vues ajoutées par ChatGPT (2026-10-27). De dos : hanches 8 px sous le haut du dossier, la tête dépasse.
  'ch_cc-fauteuil-club-dos': [[32, 24]],
  'ch_cc-fauteuil-accueil-dos': [[16, 26]], 'ch_cc-fauteuil-accueil-droite': [[13, 46]], 'ch_cc-fauteuil-accueil-gauche': [[19, 46]],
  'ch_cc-pouf-dos': [[16, 12]], 'ch_cc-pouf-droite': [[16, 12]], 'ch_cc-pouf-gauche': [[16, 12]],
  'ch_cc-canape-dos': [[25, 14], [48, 14], [71, 14]],
  'ch_cc-canape-droite': [[26, 34], [26, 64], [26, 94]], 'ch_cc-canape-gauche': [[38, 34], [38, 64], [38, 94]],
  'ch_cc-canape-deux-face': [[21, 34], [43, 34]], 'ch_cc-canape-deux-dos': [[21, 16], [43, 16]],
  'ch_cc-canape-deux-gauche': [[38, 34], [38, 64]],
  'ch_cc-fauteuil-ergonomique-face': [[16, 44]], 'ch_cc-fauteuil-cocon-face': [[32, 38]],
  'ch_mb-chaise-face': [[16, 18]], 'ch_mb-chaise-dos': [[16, 17]], 'ch_mb-chaise-droite': [[12, 18]], 'ch_mb-chaise-gauche': [[19, 18]],
  // Atlas de la PR #32 (monde-du-campus) : chaise, fauteuil, canapé, banc, chaise du pupitre.
  'ch_at-chaise-face': [[16, 16]], 'ch_at-chaise-dos': [[16, 14]], 'ch_at-chaise-droite': [[13, 17]], 'ch_at-chaise-gauche': [[18, 17]],
  'ch_at-fauteuil-lounge-face': [[16, 18]], 'ch_at-fauteuil-lounge-dos': [[16, 20]],
  'ch_at-fauteuil-lounge-droite': [[14, 18]], 'ch_at-fauteuil-lounge-gauche': [[18, 18]],
  'ch_at-canape-face': [[21, 17], [43, 17]], 'ch_at-canape-dos': [[21, 16], [43, 16]],
  'ch_at-canape-droite': [[14, 20], [14, 44]], 'ch_at-canape-gauche': [[18, 20], [18, 44]],
  // Bancs : réglés sur les images du plan 66ad6e1 (banc de 48 px de large, x 8 à 56) ;
  // les anciennes valeurs (x 32 et 64) venaient d'une image plus large et posaient l'avatar à côté.
  'ch_at-banc-face': [[20, 40], [44, 40]], 'ch_at-banc-dos': [[20, 14], [44, 14]],
  'ch_at-banc-droite': [[14, 44]], 'ch_at-banc-gauche': [[18, 44]],
  'ch_at-chaise-table-face': [[16, 16]], 'ch_at-chaise-table-dos': [[16, 8]],
  'ch_at-chaise-table-droite': [[20, 17]], 'ch_at-chaise-table-gauche': [[11, 17]],
  'ch_at-pupitre-chaise-face': [[32, 24]], 'ch_at-pupitre-chaise-dos': [[32, 12]],
  'ch_at-pupitre-droite': [[17, 36]], 'ch_at-pupitre-gauche': [[47, 36]],
  'ch_mb-fauteuil-face': [[16, 19]], 'ch_mb-fauteuil-dos': [[16, 17]], 'ch_mb-fauteuil-droite': [[13, 19]], 'ch_mb-fauteuil-gauche': [[18, 19]],
};
const HIP_ABOVE_ANCHOR = 9;
const HIP_BEHIND: Record<SeatDirection, number> = { right: 4, left: -4, down: 0, up: 0 };

/**
 * Places d'un siège : `dx`, `dy` = ancre de l'avatar assis (bas-milieu de sa
 * case) depuis le coin haut gauche de l'image du siège, `lift` = relèvement de
 * l'image. Siège sans réglage (fauteuils sur roulettes `pl_fauteuil_*`, autres) :
 * une place au milieu du bas de l'image, relevée de 16 ou 12 px.
 */
export function seatSpots(furnitureId: string, direction: SeatDirection, w: number, h: number): Array<{ dx: number; dy: number; lift: number }> {
  const hips = HIPS[furnitureId];
  if (hips) return hips.map(([hx, hy]) => ({ dx: hx + HIP_BEHIND[direction], dy: hy + HIP_ABOVE_ANCHOR, lift: 0 }));
  return [{ dx: w / 2, dy: h, lift: furnitureId.startsWith('pl_fauteuil_') ? 16 : 12 }];
}

/** Siège occupé : position de l'ancre de l'avatar (bas-centre de la case du siège), direction, relèvement, frappe. */
export interface Seat {
  x: number;
  y: number;
  direction: SeatDirection;
  lift: number;
  /** L'avatar tape au clavier (touche C, assis). */
  typing: boolean;
}

export function isSeatDirection(value: unknown): value is SeatDirection {
  return value === 'up' || value === 'down' || value === 'left' || value === 'right';
}

/** Forme diffusée dans `PlayerState.seat` : `x,y,direction,lift[,t]`, vide = debout. */
export function formatSeat(seat: Seat | null): string {
  if (!seat) return '';
  return `${Math.round(seat.x)},${Math.round(seat.y)},${seat.direction},${Math.round(seat.lift)}${seat.typing ? ',t' : ''}`;
}

export function parseSeat(value: unknown): Seat | null {
  if (typeof value !== 'string' || !value) return null;
  const [x, y, direction, lift, t] = value.split(',');
  const nx = Number(x), ny = Number(y), nl = Number(lift);
  if (!Number.isFinite(nx) || !Number.isFinite(ny) || !isSeatDirection(direction)) return null;
  return { x: nx, y: ny, direction, lift: Number.isFinite(nl) ? nl : 0, typing: t === 't' };
}

/** Deux sièges au même endroit (même case) ? */
export function sameSeatSpot(a: Pick<Seat, 'x' | 'y'>, b: Pick<Seat, 'x' | 'y'>): boolean {
  return Math.round(a.x) === Math.round(b.x) && Math.round(a.y) === Math.round(b.y);
}
